import React, { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FaCheck } from "react-icons/fa";
import { AddLeadSlice } from "../../redux/features/LeadManagementSlice";
import LeadModal from "./components/LeadModal.jsx";
import { eventCategories, todayInputValue } from "./leadConstants";

const initialFormData = {
  name: "",
  phone: "",
  categoryId: "",
  services: [],
  decorationName: "",
  eventDate: "",
  eventTime: "",
  address: "",
};

const PHONE_REGEX = /^[6-9]\d{9}$/;

const validate = (formData) => {
  const errors = {};

  if (!formData.name.trim()) errors.name = "Customer name is required";

  if (!formData.phone) errors.phone = "Phone number is required";
  else if (!PHONE_REGEX.test(formData.phone)) errors.phone = "Enter a valid 10 digit mobile number";

  if (!formData.categoryId) errors.categoryId = "Please select an event category";
  else if (formData.services.length === 0) errors.services = "Select at least one service";

  if (!formData.address.trim()) errors.address = "Address is required";

  return errors;
};

const FieldError = ({ message }) =>
  message ? <span className="lm-field-error">{message}</span> : null;

const AddLeadModal = ({ isOpen, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const { addLeadLoading } = useSelector((state) => state.leadManagement);

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});

  const selectedCategory = useMemo(
    () => eventCategories.find((category) => category.id === formData.categoryId),
    [formData.categoryId]
  );

  const clearError = (field) =>
    setErrors((prev) => (prev[field] ? { ...prev, [field]: "" } : prev));

  const handleChange = (e) => {
    const { name, value } = e.target;
    const nextValue = name === "phone" ? value.replace(/\D/g, "").slice(0, 10) : value;

    setFormData((prev) => ({ ...prev, [name]: nextValue }));
    clearError(name);
  };

  const handleCategoryChange = (e) => {
    // Services differ per category, so reset the selection on change
    setFormData((prev) => ({ ...prev, categoryId: e.target.value, services: [] }));
    clearError("categoryId");
    clearError("services");
  };

  const toggleService = (serviceName) => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.includes(serviceName)
        ? prev.services.filter((item) => item !== serviceName)
        : [...prev.services, serviceName],
    }));
    clearError("services");
  };

  const toggleAllServices = () => {
    if (!selectedCategory) return;
    const allSelected = formData.services.length === selectedCategory.services.length;

    setFormData((prev) => ({
      ...prev,
      services: allSelected ? [] : selectedCategory.services.map((service) => service.name),
    }));
    clearError("services");
  };

  const resetAndClose = useCallback(() => {
    if (addLeadLoading) return;
    setFormData(initialFormData);
    setErrors({});
    onClose();
  }, [addLeadLoading, onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate(formData);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    const payload = {
      name: formData.name.trim(),
      phoneno: formData.phone,
      address: formData.address.trim(),
      decorationName: formData.decorationName.trim(),
      categoryName: selectedCategory.name,
      services: formData.services,
      // Legacy flags still read elsewhere — derive them from the selected services
      isCatering: formData.services.includes("Catering"),
      isPhotography: formData.services.includes("Photography"),
      ...(formData.eventDate && { eventDate: new Date(formData.eventDate).toISOString() }),
      ...(formData.eventTime && { eventTime: formData.eventTime }),
    };

    try {
      const result = await dispatch(AddLeadSlice(payload)).unwrap();
      setFormData(initialFormData);
      setErrors({});
      onClose();
      onSuccess?.(result?.data);
    } catch {
      // Error toast is handled in the slice
    }
  };

  const allServicesSelected =
    selectedCategory && formData.services.length === selectedCategory.services.length;

  const footer = (
    <>
      <button type="button" className="lm-btn lm-btn--ghost" onClick={resetAndClose} disabled={addLeadLoading}>
        Cancel
      </button>
      <button type="submit" form="add-lead-form" className="lm-btn lm-btn--primary" disabled={addLeadLoading}>
        {addLeadLoading ? (
          <>
            <span className="lm-spinner" /> Adding Lead...
          </>
        ) : (
          "Add Lead"
        )}
      </button>
    </>
  );

  return (
    <LeadModal
      isOpen={isOpen}
      onClose={resetAndClose}
      title="Add New Lead"
      subtitle="Capture customer details and the services they are interested in"
      footer={footer}
      disableClose={addLeadLoading}
      size="lg"
    >
      <form id="add-lead-form" onSubmit={handleSubmit} noValidate>
        <h6 className="lm-section-title">Customer Details</h6>

        <div className="lm-form-grid">
          <div className="lm-field">
            <label htmlFor="lead-name">
              Customer Name <span className="lm-required">*</span>
            </label>
            <input
              id="lead-name"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter customer name"
              className={`lm-input ${errors.name ? "is-invalid" : ""}`}
              autoFocus
            />
            <FieldError message={errors.name} />
          </div>

          <div className="lm-field">
            <label htmlFor="lead-phone">
              Phone Number <span className="lm-required">*</span>
            </label>
            <div className={`lm-input-group ${errors.phone ? "is-invalid" : ""}`}>
              <span className="lm-input-prefix">+91</span>
              <input
                id="lead-phone"
                type="tel"
                name="phone"
                inputMode="numeric"
                value={formData.phone}
                onChange={handleChange}
                placeholder="10 digit mobile number"
              />
            </div>
            <FieldError message={errors.phone} />
          </div>

          <div className="lm-field lm-field--full">
            <label htmlFor="lead-address">
              Address <span className="lm-required">*</span>
            </label>
            <textarea
              id="lead-address"
              name="address"
              rows={2}
              value={formData.address}
              onChange={handleChange}
              placeholder="Enter event / customer address"
              className={`lm-input ${errors.address ? "is-invalid" : ""}`}
            />
            <FieldError message={errors.address} />
          </div>
        </div>

        <h6 className="lm-section-title">Event Details</h6>

        <div className="lm-form-grid">
          <div className="lm-field">
            <label htmlFor="lead-category">
              Event Category <span className="lm-required">*</span>
            </label>
            <select
              id="lead-category"
              name="categoryId"
              value={formData.categoryId}
              onChange={handleCategoryChange}
              className={`lm-input lm-select ${errors.categoryId ? "is-invalid" : ""}`}
            >
              <option value="">Select category</option>
              {eventCategories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
            <FieldError message={errors.categoryId} />
          </div>

          <div className="lm-field">
            <label htmlFor="lead-decoration">Decoration / Package Name</label>
            <input
              id="lead-decoration"
              type="text"
              name="decorationName"
              value={formData.decorationName}
              onChange={handleChange}
              placeholder="e.g. Floral Theme Decoration"
              className="lm-input"
            />
          </div>

          <div className="lm-field">
            <label htmlFor="lead-event-date">Event Date</label>
            <input
              id="lead-event-date"
              type="date"
              name="eventDate"
              min={todayInputValue()}
              value={formData.eventDate}
              onChange={handleChange}
              className="lm-input"
            />
          </div>

          <div className="lm-field">
            <label htmlFor="lead-event-time">Event Time</label>
            <input
              id="lead-event-time"
              type="time"
              name="eventTime"
              value={formData.eventTime}
              onChange={handleChange}
              className="lm-input"
            />
          </div>
        </div>

        <div className="lm-services">
          <div className="lm-services-header">
            <div>
              <h6 className="lm-section-title mb-0">
                Services Required {selectedCategory && <span className="lm-required">*</span>}
              </h6>
              {selectedCategory && (
                <small className="lm-muted">
                  {formData.services.length} of {selectedCategory.services.length} selected
                </small>
              )}
            </div>

            {selectedCategory && (
              <button type="button" className="lm-link-btn" onClick={toggleAllServices}>
                {allServicesSelected ? "Clear all" : "Select all"}
              </button>
            )}
          </div>

          {selectedCategory ? (
            <div className="lm-service-grid">
              {selectedCategory.services.map((service) => {
                const checked = formData.services.includes(service.name);
                return (
                  <label
                    key={service.id}
                    className={`lm-service-chip ${checked ? "is-checked" : ""}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleService(service.name)}
                    />
                    <span className="lm-service-check">{checked && <FaCheck size={9} />}</span>
                    <span>{service.name}</span>
                  </label>
                );
              })}
            </div>
          ) : (
            <div className="lm-services-empty">
              Select an event category to see the available services
            </div>
          )}
          <FieldError message={errors.services} />
        </div>
      </form>
    </LeadModal>
  );
};

export default AddLeadModal;
